<template>
  <div
    v-if="decision"
    class="decision-escalation-timeline mt-5 pt-4 border-t border-[#F0F0F0]"
    data-testid="decision-escalation-timeline"
  >
    <div class="flex items-center gap-2 mb-3">
      <i class="pi pi-clock text-[var(--color-disabled)] text-sm" aria-hidden="true" />
      <p class="text-[10px] uppercase tracking-wider text-[#ABABAB] font-medium m-0">
        {{ $t('UserProjects.decisions.timeline.title') }}
      </p>
    </div>

    <div class="grid grid-cols-3 gap-3 mb-3">
      <div
        v-for="step in steps"
        :key="step.key"
        class="rounded-[8px] p-2.5 bg-[#F8F8F8] border border-[#F0F0F0]"
        :data-testid="`decision-timeline-step-${step.key}`"
      >
        <p class="text-[10px] text-[var(--color-disabled)] mb-0.5 m-0">
          {{ $t(`UserProjects.decisions.timeline.steps.${step.key}`) }}
        </p>
        <p class="text-[12px] font-medium text-[var(--color-dark)] m-0">{{ step.value }}</p>
        <p
          class="text-[11px] m-0 mt-0.5"
          :class="step.detailTone === 'error' ? 'text-[var(--color-error-text)]' : 'text-[#ABABAB]'"
        >
          {{ step.detail }}
        </p>
      </div>
    </div>

    <div class="flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
      <span class="inline-flex items-center gap-1">
        <i class="pi pi-clock text-[var(--color-disabled)] text-xs" aria-hidden="true" />
        <span class="text-[var(--color-disabled)]">{{ $t('UserProjects.decisions.timeline.sinceCreation') }}:</span>
        <span class="font-medium text-[var(--color-dark)]">{{ sinceCreationLabel }}</span>
      </span>
      <span class="inline-flex items-center gap-1">
        <i
          class="pi pi-exclamation-triangle text-xs"
          :class="timing.isOverdue ? 'text-[var(--color-error-text)]' : 'text-[#ABABAB]'"
          aria-hidden="true"
        />
        <span class="text-[var(--color-disabled)]">
          {{ timing.isOverdue
            ? $t('UserProjects.decisions.timeline.overdueSince')
            : $t('UserProjects.decisions.timeline.timeUntilDue') }}:
        </span>
        <span
          class="font-medium"
          :class="timing.isOverdue ? 'text-[var(--color-error-text)]' : 'text-[var(--color-success-text)]'"
        >
          {{ dueTimingLabel }}
        </span>
      </span>
      <span
        v-if="timing.showEscalationHighlight"
        class="inline-flex items-center gap-1 text-[var(--color-error-text)] font-medium"
      >
        <i class="pi pi-arrow-up text-xs" aria-hidden="true" />
        {{ $t('UserProjects.decisions.timeline.escalationLevel', { level: escalationLabel }) }}
      </span>
    </div>
  </div>
</template>

<script>
import {
  buildDecisionTimelineSteps,
  buildDecisionTimingMeta,
  escalationTierFromLevel,
  formatDaysCount,
  ESCALATION_TIER_STYLES,
} from '@/utils/decisionDisplay';

export default {
  name: 'DecisionEscalationTimeline',
  props: {
    decision: {
      type: Object,
      default: null,
    },
  },
  computed: {
    steps() {
      return buildDecisionTimelineSteps(this.decision, this.$i18n.locale);
    },
    timing() {
      return buildDecisionTimingMeta(this.decision, this.$i18n.locale);
    },
    sinceCreationLabel() {
      return formatDaysCount(this.timing.daysSinceCreation, this.$i18n.locale);
    },
    dueTimingLabel() {
      if (this.timing.dueTimingDays === null) return '—';
      if (this.timing.isOverdue) {
        return formatDaysCount(this.timing.dueTimingDays, this.$i18n.locale);
      }
      if (this.timing.dueTimingDays <= 0) return '—';
      return formatDaysCount(this.timing.dueTimingDays, this.$i18n.locale);
    },
    escalationLabel() {
      const tier = escalationTierFromLevel(this.decision?.escalationLevel);
      return this.$t(`UserProjects.decisions.escalation.${ESCALATION_TIER_STYLES[tier].labelKey}`);
    },
  },
};
</script>
